/**
 * Copies text, resolving to whether it worked.
 *
 * The execCommand fallback matters: navigator.clipboard requires a secure
 * context, and captains and curators in the field hit this from in-app browsers
 * that don't always provide one. Without it the button would silently no-op.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const scratch = document.createElement("textarea");
    scratch.value = text;
    scratch.style.position = "fixed";
    scratch.style.opacity = "0";
    document.body.appendChild(scratch);
    scratch.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(scratch);
    return ok;
  } catch {
    return false;
  }
}

export function buildWhatsAppMessage(productTitle?: string): string {
  return productTitle
    ? `Hi, I'm interested in your listing "${productTitle}" on NineFinds. Is it still available?`
    : `Hi, I'm interested in your listing on NineFinds. Is it still available?`;
}

/**
 * wa.me deep link with an arbitrary prefilled message.
 *
 * Kept separate from buildWhatsAppUrl because that one hardcodes listing-enquiry
 * copy — any other context (a captain contacting a prospect, say) needs its own
 * wording but the same number handling.
 *
 * A bare 10-digit number is assumed Indian and prefixed with 91; anything longer
 * is treated as already carrying its country code, so a stored "+91 98450 12345"
 * doesn't become "9191...".
 */
export function buildWhatsAppUrlWithMessage(
  phone: string,
  message: string
): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "#";
  const withCountryCode = digits.length <= 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCountryCode}?text=${encodeURIComponent(message)}`;
}

export function buildWhatsAppUrl(phone: string, productTitle?: string): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "#";
  return `https://wa.me/+91${digits}?text=${encodeURIComponent(buildWhatsAppMessage(productTitle))}`;
}

export const DEFAULT_ORDER_MESSAGE =
  "Hello! I'm sharing this collection to place an order. Please find my delivery address below. Let me know if you need any additional details.";

/** Universal Google Maps URL that opens a pinned location on web or in the Maps app. */
export function buildGoogleMapsLink(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/**
 * Same universal Maps URL, but searching free text instead of coordinates — for
 * rows that carry a written location and no pin. Less precise by nature: Maps
 * resolves "Jayanagar 4th Block" to its own best guess, so prefer
 * buildGoogleMapsLink whenever lat/lng exist.
 */
export function buildGoogleMapsSearchLink(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function buildWhatsAppOrderUrl(
  phone: string,
  address: string,
  message: string,
  collectionUrl: string,
  locationLink?: string,
  customerPhone?: string
): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "#";
  const locationLine = locationLink ? `\n\n📍 Delivery pin: ${locationLink}` : "";
  const contactLine = customerPhone ? `\n\nContact number: +91${customerPhone}` : "";
  const text = `${message}\n\nDelivery address: ${address}${locationLine}${contactLine}\n\nCollection: ${collectionUrl}`;
  return `https://wa.me/+91${digits}?text=${encodeURIComponent(text)}`;
}

/** wa.me link with no recipient: WhatsApp asks which chat to send it to. */
export function buildWhatsAppShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

/** tel: link with spaces/formatting stripped (digits and a leading +). */
export function buildTelLink(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}
